/** All runtime settings grouped by `group`, each with a typed input and its own Save (announced). Saving needs the
 * `admin` scope (PATCH /api/settings); everyone else sees the values read-only. */
import { useEffect, useState } from "react";
import { api, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { useAction } from "../lib/actions";
import { fmtWhen } from "../lib/time";
import { ActionError, QueryView } from "../lib/ui";
import { AdminOnlyNote } from "./AdminOnly";
import { groupSettings, parseSettingInput, settingInputText, type TypedSetting, useSettings } from "./typed";

const inputId = (key: string) => `setting-${key.replace(/[^a-zA-Z0-9_-]/g, "-")}`;

function SettingValue({ s }: { s: TypedSetting }) {
  return (
    <div className="ws7-setting" data-testid={`setting-${s.key}`}>
      <div>
        <span className="ws7-mono">{s.key}</span>
        <div className="ws7-muted">{s.description}</div>
        <div className="ws7-muted">
          {s.type} · default {JSON.stringify(s.default)}
          {s.updated_at ? ` · set by ${s.actor ?? "?"} at ${fmtWhen(s.updated_at)}` : ""}
        </div>
      </div>
      <div className="ws7-mono" data-testid={`setting-value-${s.key}`}>{JSON.stringify(s.value)}</div>
    </div>
  );
}

function SettingRow({ s }: { s: TypedSetting }) {
  const [text, setText] = useState(settingInputText(s.type, s.value));
  const [checked, setChecked] = useState(s.value === true);
  useEffect(() => {
    setText(settingInputText(s.type, s.value));
    setChecked(s.value === true);
  }, [s.type, s.value]);
  const save = useAction(
    (value: unknown) => unwrap(api.PATCH("/api/settings", { body: { [s.key]: value } })),
    {
      label: `Save ${s.key}`,
      success: (_r, value) => `Saved ${s.key} = ${JSON.stringify(value)}`,
      invalidate: [qk.settings, qk.list("sources.health")],
    },
  );
  const parsed = parseSettingInput(s.type, s.type === "bool" ? checked : text);
  const dirty = parsed.ok ? JSON.stringify(parsed.value) !== JSON.stringify(s.value) : true;
  const isDefault = JSON.stringify(s.value) === JSON.stringify(s.default);
  const id = inputId(s.key);
  const errId = `${id}-err`;
  return (
    <div className="ws7-setting" data-testid={`setting-${s.key}`}>
      <div>
        <label htmlFor={id} className="ws7-mono">{s.key}</label>
        <div className="ws7-muted">{s.description}</div>
        <div className="ws7-muted">
          {s.type} · default {JSON.stringify(s.default)}
          {s.updated_at ? ` · set by ${s.actor ?? "?"} at ${fmtWhen(s.updated_at)}` : ""}
        </div>
      </div>
      <div>
        {s.type === "bool" ? (
          <input id={id} type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
        ) : (
          <input
            id={id}
            type={s.type === "int" || s.type === "float" ? "number" : "text"}
            step={s.type === "int" ? 1 : s.type === "float" ? "any" : undefined}
            inputMode={s.type === "int" ? "numeric" : s.type === "float" ? "decimal" : undefined}
            value={text}
            aria-invalid={!parsed.ok}
            aria-describedby={!parsed.ok ? errId : undefined}
            onChange={(e) => setText(e.target.value)}
          />
        )}
        {!parsed.ok && <div id={errId} className="ws7-field-error">{s.key} {parsed.error}</div>}
        <ActionError error={save.error} />
      </div>
      <div className="ws7-inline">
        <button type="button" className="ws7-primary" data-testid={`setting-save-${s.key}`} aria-label={`Save ${s.key}`}
          disabled={!parsed.ok || !dirty || save.pending} onClick={() => parsed.ok && void save.run(parsed.value)}>
          Save
        </button>
        <button type="button" data-testid={`setting-reset-${s.key}`} aria-label={`Reset ${s.key} to default`}
          disabled={isDefault || save.pending} onClick={() => void save.run(s.default)}>
          Reset
        </button>
      </div>
    </div>
  );
}

export function SettingsTab({ admin }: { admin: boolean }) {
  const q = useSettings();
  return (
    <QueryView q={q} what="settings">
      {(list) => (
        <div data-testid="settings-groups">
          {!admin && <AdminOnlyNote what="changing settings (Save / Reset)" testId="settings-admin-only" />}
          {groupSettings(list).map(([group, items]) => (
            <fieldset key={group} className="ws7-setting-group" data-testid={`settings-group-${group}`}>
              <legend>{group}</legend>
              {items.map((s) => (admin ? <SettingRow key={s.key} s={s} /> : <SettingValue key={s.key} s={s} />))}
            </fieldset>
          ))}
        </div>
      )}
    </QueryView>
  );
}
